import {
  Box,
  Button,
  FormControl,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import Alert from 'components/features/Alert';
import { PAGE_LIST } from 'constant/pages';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { saveKeyToLocalStorage } from 'utils/constant';
import { commonErrorMessage } from 'utils/error-message';
import { postAuthLogin } from 'utils/fetch';
import ja from 'utils/ja';

interface SettingFormType {
  url: string;
  appId: string;
  appKey: string;
  dbId: string;
  dbKey: string;
}

const SETTING_FORM_DEFAULT_VALUE = {
  url: process.env.REACT_APP_CLUSTER_DB_API ?? '',
  appId: '',
  appKey: '',
  dbId: '',
  dbKey: '',
};

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ISettingProps {}

const Setting: React.FC<ISettingProps> = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  const { control, handleSubmit, watch, setError } = useForm<SettingFormType>({
    defaultValues: SETTING_FORM_DEFAULT_VALUE,
  });

  const { url } = watch();

  const [configError, setConfigError] = useState<boolean>(false);

  const onSubmit = async (data: SettingFormType) => {
    try {
      await fetch(data.url + '/health');
      localStorage.setItem('url', url);
    } catch (error) {
      setError('url', {
        type: 'custom',
        message: ja.message_error.url_invalid,
      });
      return;
    }
    try {
      await postAuthLogin({
        appId: data.appId,
        appKey: data.appKey,
        dbId: data.dbId,
        dbKey: data.dbKey,
      });
      saveKeyToLocalStorage('appId', data.appId);
      saveKeyToLocalStorage('appKey', data.appKey);
      saveKeyToLocalStorage('dbId', data.dbId);
      saveKeyToLocalStorage('dbKey', data.dbKey);
      navigate(PAGE_LIST.HOME.pathname());
    } catch (error) {
      setConfigError(true);
    }
  };

  return (
    <Box
      sx={{
        width: '840px',
        boxShadow: '0px 2px 4px 2px #00000030',
        borderRadius: '10px',
        padding: '20px',
      }}
    >
      <Box
        component='form'
        onSubmit={handleSubmit(onSubmit)}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          gap: '20px',
          '& *': { fontSize: '40px !important' },
        }}
      >
        <Box>
          <Controller
            name='url'
            control={control}
            rules={{
              required: true,
              pattern: {
                value:
                  /^(http|ftp|https):\/\/[\w-]+(\.[\w-]+)+([\w.,@?^=%&amp;:/~+#-]*[\w@?^=%&amp;/~+#-])?/,
                message: ja.message_error.format_url,
              },
            }}
            render={({ field, fieldState }) => (
              <FormControl variant='standard' fullWidth>
                <Typography component='p' mb='15px'>
                  {ja.setting.url.label}
                  <Typography component='span' color={theme.palette.error.main}>
                    *
                  </Typography>
                </Typography>
                <TextField
                  id='url'
                  placeholder={ja.setting.url.placeholder}
                  {...field}
                />
                {fieldState.error && (
                  <Typography
                    sx={{
                      color: theme.palette.error.main,
                      fontSize: theme.typography.fontSize,
                      my: '5px',
                    }}
                  >
                    {fieldState.error.type === 'pattern'
                      ? fieldState.error.message
                      : commonErrorMessage.MSE00003(ja.setting.url.label)}
                  </Typography>
                )}
              </FormControl>
            )}
          />
          <Controller
            name='appId'
            control={control}
            rules={{
              required: true,
            }}
            render={({ field, fieldState }) => (
              <FormControl variant='standard' fullWidth sx={{ mt: '20px' }}>
                <Typography component='p' mb='15px'>
                  {ja.setting.app_id.label}
                  <Typography component='span' color={theme.palette.error.main}>
                    *
                  </Typography>
                </Typography>
                <TextField
                  id='appId'
                  placeholder={ja.setting.app_id.placeholder}
                  {...field}
                />
                {fieldState.error && (
                  <Typography
                    sx={{
                      color: theme.palette.error.main,
                      fontSize: theme.typography.fontSize,
                      my: '5px',
                    }}
                  >
                    {commonErrorMessage.MSE00003(ja.setting.app_id.label)}
                  </Typography>
                )}
              </FormControl>
            )}
          />
          <Controller
            name='appKey'
            control={control}
            rules={{
              required: true,
            }}
            render={({ field, fieldState }) => (
              <FormControl variant='standard' fullWidth sx={{ mt: '20px' }}>
                <Typography component='p' mb='15px'>
                  {ja.setting.app_key.label}
                  <Typography component='span' color={theme.palette.error.main}>
                    *
                  </Typography>
                </Typography>
                <TextField
                  id='appKey'
                  placeholder={ja.setting.app_key.placeholder}
                  {...field}
                />
                {fieldState.error && (
                  <Typography
                    sx={{
                      color: theme.palette.error.main,
                      fontSize: theme.typography.fontSize,
                      my: '5px',
                    }}
                  >
                    {commonErrorMessage.MSE00003(ja.setting.app_key.label)}
                  </Typography>
                )}
              </FormControl>
            )}
          />
          <Controller
            name='dbId'
            control={control}
            rules={{
              required: true,
            }}
            render={({ field, fieldState }) => (
              <FormControl variant='standard' fullWidth sx={{ mt: '20px' }}>
                <Typography component='p' mb='15px'>
                  {ja.setting.db_id.label}
                  <Typography component='span' color={theme.palette.error.main}>
                    *
                  </Typography>
                </Typography>
                <TextField
                  id='dbId'
                  placeholder={ja.setting.db_id.placeholder}
                  {...field}
                />
                {fieldState.error && (
                  <Typography
                    sx={{
                      color: theme.palette.error.main,
                      fontSize: theme.typography.fontSize,
                      my: '5px',
                    }}
                  >
                    {commonErrorMessage.MSE00003(ja.setting.db_id.label)}
                  </Typography>
                )}
              </FormControl>
            )}
          />
          <Controller
            name='dbKey'
            control={control}
            rules={{
              required: true,
            }}
            render={({ field, fieldState }) => (
              <FormControl variant='standard' fullWidth sx={{ mt: '20px' }}>
                <Typography component='p' mb='15px'>
                  {ja.setting.db_key.label}
                  <Typography component='span' color={theme.palette.error.main}>
                    *
                  </Typography>
                </Typography>
                <TextField
                  id='dbKey'
                  placeholder={ja.setting.db_key.placeholder}
                  {...field}
                />
                {fieldState.error && (
                  <Typography
                    sx={{
                      color: theme.palette.error.main,
                      fontSize: theme.typography.fontSize,
                      my: '5px',
                    }}
                  >
                    {commonErrorMessage.MSE00003(ja.setting.db_key.label)}
                  </Typography>
                )}
              </FormControl>
            )}
          />
          <Box sx={{ textAlign: 'right', mt: '20px' }}>
            <Button
              type='submit'
              sx={{
                fontSize: '35px !important',
                lineHeight: '35px',
              }}
            >
              {ja.setting.next}
            </Button>
          </Box>
        </Box>
      </Box>
      <Alert
        open={configError}
        onClose={() => setConfigError(false)}
        message={ja.message_error.app_config}
        type='error'
      />
    </Box>
  );
};

export default Setting;
